import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building, ChevronDown, Check, Users, Zap, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/customSupabaseClient';

const TenantSwitcher = () => {
  const { currentTenant, switchTenant, user } = useAuth();
  const { toast } = useToast();
  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch user's tenants
  useEffect(() => {
    const fetchUserTenants = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('user_tenants')
          .select(`
            tenant_id,
            tenants (
              id,
              company_name,
              subscription_plan,
              is_active,
              created_at
            )
          `)
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching tenants:', error);
          return;
        }

        const tenantList = data?.map(item => item.tenants).filter(Boolean) || [];
        setTenants(tenantList);

        // Set current tenant if none is selected
        if (!currentTenant && tenantList.length > 0) {
          const activeTenant = tenantList.find(t => t.is_active) || tenantList[0];
          switchTenant(activeTenant.id);
        }
      } catch (error) {
        console.error('Error fetching tenants:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserTenants();
  }, [user, currentTenant, switchTenant]);

  const handleTenantSwitch = async (tenantId) => {
    try {
      await switchTenant(tenantId);
      setIsOpen(false);
    } catch (error) {
      console.error('Error switching tenant:', error);
    }
  };

  if (!user || tenants.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex items-center space-x-2 text-white hover:bg-slate-800/50 transition-all duration-200"
          >
            <Building className="w-4 h-4" />
            <span className="font-medium">
              {currentTenant?.company_name || 'Select Company'}
            </span>
            <motion.div
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-4 h-4" />
            </motion.div>
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          align="end" 
          className="w-80 bg-slate-900 border-slate-700 text-white"
          sideOffset={8}
        >
          <div className="p-4 border-b border-slate-700">
            <h3 className="font-semibold text-white mb-2">Switch Company</h3>
            <p className="text-sm text-slate-400">
              Manage different field operations and teams
            </p>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center">
                <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-sm text-slate-400 mt-2">Loading companies...</p>
              </div>
            ) : (
              <AnimatePresence>
                {tenants.map((tenant, index) => (
                  <motion.div
                    key={tenant.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <DropdownMenuItem
                      onClick={() => handleTenantSwitch(tenant.id)}
                      className={`flex items-center justify-between p-4 hover:bg-slate-800/50 cursor-pointer transition-all duration-200 ${
                        currentTenant?.id === tenant.id ? 'bg-slate-800/30' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                          <Building className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium text-white">{tenant.company_name}</h4>
                            {tenant.is_active && (
                              <span className="px-2 py-1 text-xs bg-green-600 text-white rounded-full">
                                Active
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-400">{tenant.subscription_plan}</p>
                        </div>
                      </div>
                      
                      {currentTenant?.id === tenant.id && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-purple-400"
                        >
                          <Check className="w-5 h-5" />
                        </motion.div>
                      )}
                    </DropdownMenuItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
          
          {/* Compelling CTA for new tenant creation */}
          <div className="p-4 border-t border-slate-700 bg-gradient-to-r from-purple-600/10 to-blue-600/10">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <div>
                <h4 className="font-medium text-white">Create New Company</h4>
                <p className="text-xs text-slate-400">Expand your operations</p>
              </div>
            </div>
            <Button 
              size="sm" 
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
            >
              + Add New Company
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default TenantSwitcher;
